import { db } from '../../../config/firebaseConfig';
import { 
  collection, 
  getDocs, 
  query, 
  where, 
  getDoc, 
  doc,
  Timestamp,
  limit,
  orderBy,
  getCountFromServer
} from 'firebase/firestore';

// Cache for storing previously fetched data
const statsCache = {
  data: null,
  timestamp: null,
  // Cache expiration time in milliseconds (30 minutes)
  expirationTime: 30 * 60 * 1000
};

// Local storage key for backup data
const ADMIN_STATS_STORAGE_KEY = 'resumedesign_admin_stats';

// Default stats to show while loading - ensure all values are numbers
const defaultStats = {
  // User Metrics
  totalUsers: 0,
  newUsersToday: 0,
  newUsersWeek: 0,
  newUsersMonth: 0,
  activeSubscribers: 0,
  freeUsers: 0,
  
  // Subscription Metrics
  conversionRate: "0.00",
  churnRate: "0.00",
  averageLifetimeValue: "0.00",
  newSubscribersToday: 0,
  newSubscribersWeek: 0,
  newSubscribersMonth: 0,
  
  // Document Metrics
  totalResumes: 0,
  totalCoverLetters: 0,
  resumesCreatedToday: 0,
  coverLettersCreatedToday: 0,
  resumesCreatedWeek: 0,
  coverLettersCreatedWeek: 0,
  
  // Financial Metrics - USD
  revenueToday_USD: "0.00",
  revenueWeek_USD: "0.00",
  revenueMonth_USD: "0.00",
  revenueYear_USD: "0.00",
  averageOrderValue_USD: "0.00",
  
  // Financial Metrics - INR
  revenueToday_INR: "0.00",
  revenueWeek_INR: "0.00",
  revenueMonth_INR: "0.00",
  revenueYear_INR: "0.00",
  averageOrderValue_INR: "0.00",
  
  // Payment Analytics
  successfulPayments: 0,
  failedPayments: 0,
  paymentAttempts: 0,
  paymentSuccessRate: "0.00",
  
  // Engagement Metrics
  averageDocumentsPerUser: "0.00",
  activeUsersToday: 0,
  activeUsersWeek: 0,
  
  // Plan Distribution
  sevenDaySubscribers: 0,
  oneMonthSubscribers: 0,
  threeMonthSubscribers: 0
};

// Helper function to ensure a value is a number
const ensureNumber = (value, defaultValue = 0) => {
  const num = Number(value);
  return isNaN(num) || !isFinite(num) ? defaultValue : num;
};

// Helper function to ensure a value is a string
const ensureString = (value, defaultValue = "0.00") => {
  return value !== null && value !== undefined ? String(value) : defaultValue;
};

/**
 * Fetches comprehensive admin statistics
 * @param {string} userId - The ID of the user to check for admin privileges
 * @returns {Promise<Object>} - Complete admin statistics
 */
export const fetchAdminStats = async (userId) => {
  // First check if we have valid cached data
  const now = Date.now();
  if (statsCache.data && statsCache.timestamp && 
      now - statsCache.timestamp < statsCache.expirationTime) {
    console.log('Using cached admin stats data');
    return statsCache.data;
  }

  try {
    // Check admin access first
    const isAdmin = await checkAdminAccessWithRetry(userId);
    if (!isAdmin) {
      throw new Error('User does not have admin access');
    }
    
    // Try to load from localStorage first if no valid memory cache
    let initialStats = { ...defaultStats };
    try {
      const storedData = localStorage.getItem(ADMIN_STATS_STORAGE_KEY);
      if (storedData) {
        const parsedData = JSON.parse(storedData);
        console.log('Loading initial view from localStorage');
        initialStats = { ...defaultStats, ...parsedData.data };
      }
    } catch (e) {
      console.warn('Could not retrieve from localStorage', e);
    }
    
    // Try to fetch fresh data
    try {
      const stats = await fetchComprehensiveStats();
      
      // Cache the successful result
      statsCache.data = stats;
      statsCache.timestamp = Date.now();
      
      // Also save to localStorage for additional backup
      try {
        localStorage.setItem(ADMIN_STATS_STORAGE_KEY, JSON.stringify({
          data: stats,
          timestamp: Date.now()
        }));
      } catch (e) {
        console.warn('Could not save to localStorage', e);
      }
      
      return stats;
    } catch (error) {
      console.error('Error fetching fresh stats:', error);
      
      // If we have cached data that's expired but better than nothing, use it
      if (statsCache.data) {
        console.log('Using expired memory cache data as fallback');
        return statsCache.data;
      }
      
      // Return the localStorage data we loaded earlier if available
      return initialStats;
    }
  } catch (error) {
    console.error('Error in fetchAdminStats:', error);
    throw error;
  }
};

/**
 * Fetch comprehensive statistics with detailed metrics
 */
const fetchComprehensiveStats = async () => {
  const stats = { ...defaultStats };
  
  // Use Promise.allSettled to get as many successful results as possible
  const results = await Promise.allSettled([
    fetchUserMetrics(),
    fetchSubscriptionMetrics(),
    fetchDocumentMetrics(),
    fetchFinancialMetrics(),
    fetchPaymentAnalytics(),
    fetchEngagementMetrics()
  ]);
  
  // Process results, keeping defaults for any that failed
  if (results[0].status === 'fulfilled') {
    Object.assign(stats, sanitizeStats(results[0].value));
  }
  if (results[1].status === 'fulfilled') {
    Object.assign(stats, sanitizeStats(results[1].value));
  }
  if (results[2].status === 'fulfilled') {
    Object.assign(stats, sanitizeStats(results[2].value));
  }
  if (results[3].status === 'fulfilled') {
    Object.assign(stats, sanitizeStats(results[3].value));
  }
  if (results[4].status === 'fulfilled') {
    Object.assign(stats, sanitizeStats(results[4].value));
  }
  if (results[5].status === 'fulfilled') {
    Object.assign(stats, sanitizeStats(results[5].value));
  }
  
  return stats;
};

/**
 * Sanitize stats to ensure all values are proper types
 */
const sanitizeStats = (statsObject) => {
  const sanitized = {};
  
  for (const [key, value] of Object.entries(statsObject)) {
    if (key.includes('Rate') || key.includes('average') || key.includes('revenue') || key.includes('Amount')) {
      sanitized[key] = ensureString(value, "0.00");
    } else {
      sanitized[key] = ensureNumber(value, 0);
    }
  }
  
  return sanitized;
};

/**
 * Fetch user-related metrics with proper error handling
 */
const fetchUserMetrics = async () => {
  try {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    
    const [
      totalUsers,
      newUsersToday,
      newUsersWeek,
      newUsersMonth,
      activeSubscribers,
      sevenDaySubscribers,
      oneMonthSubscribers,
      threeMonthSubscribers
    ] = await Promise.allSettled([
      getCountFromServer(collection(db, 'users')),
      getCountFromServer(query(collection(db, 'users'), where('createdAt', '>=', Timestamp.fromDate(today)))),
      getCountFromServer(query(collection(db, 'users'), where('createdAt', '>=', Timestamp.fromDate(oneWeekAgo)))),
      getCountFromServer(query(collection(db, 'users'), where('createdAt', '>=', Timestamp.fromDate(oneMonthAgo)))),
      getCountFromServer(query(collection(db, 'users'), where('subscriptionStatus', '==', 'active'))),
      getCountFromServer(query(collection(db, 'users'), where('subscriptionStatus', '==', 'active'), where('planType', '==', 'sevenDays'))),
      getCountFromServer(query(collection(db, 'users'), where('subscriptionStatus', '==', 'active'), where('planType', '==', 'oneMonth'))),
      getCountFromServer(query(collection(db, 'users'), where('subscriptionStatus', '==', 'active'), where('planType', '==', 'threeMonth')))
    ]);
    
    const totalUsersCount = totalUsers.status === 'fulfilled' ? ensureNumber(totalUsers.value.data().count) : 0;
    const activeSubscribersCount = activeSubscribers.status === 'fulfilled' ? ensureNumber(activeSubscribers.value.data().count) : 0;
    
    return {
      totalUsers: totalUsersCount,
      newUsersToday: newUsersToday.status === 'fulfilled' ? ensureNumber(newUsersToday.value.data().count) : 0,
      newUsersWeek: newUsersWeek.status === 'fulfilled' ? ensureNumber(newUsersWeek.value.data().count) : 0,
      newUsersMonth: newUsersMonth.status === 'fulfilled' ? ensureNumber(newUsersMonth.value.data().count) : 0,
      activeSubscribers: activeSubscribersCount,
      freeUsers: Math.max(0, totalUsersCount - activeSubscribersCount),
      conversionRate: totalUsersCount > 0 ? ((activeSubscribersCount / totalUsersCount) * 100).toFixed(2) : "0.00",
      sevenDaySubscribers: sevenDaySubscribers.status === 'fulfilled' ? ensureNumber(sevenDaySubscribers.value.data().count) : 0,
      oneMonthSubscribers: oneMonthSubscribers.status === 'fulfilled' ? ensureNumber(oneMonthSubscribers.value.data().count) : 0,
      threeMonthSubscribers: threeMonthSubscribers.status === 'fulfilled' ? ensureNumber(threeMonthSubscribers.value.data().count) : 0
    };
  } catch (error) {
    console.error('Error fetching user metrics:', error);
    return {
      totalUsers: 0,
      newUsersToday: 0,
      newUsersWeek: 0,
      newUsersMonth: 0,
      activeSubscribers: 0,
      freeUsers: 0,
      conversionRate: "0.00",
      sevenDaySubscribers: 0,
      oneMonthSubscribers: 0,
      threeMonthSubscribers: 0
    };
  }
};

/**
 * Fetch subscription-related metrics
 */
const fetchSubscriptionMetrics = async () => {
  try {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    
    const [
      newSubscribersToday,
      newSubscribersWeek,
      newSubscribersMonth
    ] = await Promise.allSettled([
      getCountFromServer(query(
        collection(db, 'users'), 
        where('subscriptionStatus', '==', 'active'),
        where('subscriptionStartDate', '>=', Timestamp.fromDate(today))
      )),
      getCountFromServer(query(
        collection(db, 'users'), 
        where('subscriptionStatus', '==', 'active'),
        where('subscriptionStartDate', '>=', Timestamp.fromDate(oneWeekAgo))
      )),
      getCountFromServer(query(
        collection(db, 'users'), 
        where('subscriptionStatus', '==', 'active'),
        where('subscriptionStartDate', '>=', Timestamp.fromDate(oneMonthAgo))
      ))
    ]);
    
    return {
      newSubscribersToday: newSubscribersToday.status === 'fulfilled' ? ensureNumber(newSubscribersToday.value.data().count) : 0,
      newSubscribersWeek: newSubscribersWeek.status === 'fulfilled' ? ensureNumber(newSubscribersWeek.value.data().count) : 0,
      newSubscribersMonth: newSubscribersMonth.status === 'fulfilled' ? ensureNumber(newSubscribersMonth.value.data().count) : 0
    };
  } catch (error) {
    console.error('Error fetching subscription metrics:', error);
    return {
      newSubscribersToday: 0,
      newSubscribersWeek: 0,
      newSubscribersMonth: 0
    };
  }
};

/**
 * Fetch document-related metrics
 */
const fetchDocumentMetrics = async () => {
  try {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    
    const [
      totalResumes,
      totalCoverLetters,
      resumesToday,
      coverLettersToday,
      resumesWeek,
      coverLettersWeek
    ] = await Promise.allSettled([
      getCountFromServer(collection(db, 'resumes')),
      getCountFromServer(collection(db, 'cover-letters')),
      getCountFromServer(query(collection(db, 'resumes'), where('createdAt', '>=', Timestamp.fromDate(today)))),
      getCountFromServer(query(collection(db, 'cover-letters'), where('createdAt', '>=', Timestamp.fromDate(today)))),
      getCountFromServer(query(collection(db, 'resumes'), where('createdAt', '>=', Timestamp.fromDate(oneWeekAgo)))),
      getCountFromServer(query(collection(db, 'cover-letters'), where('createdAt', '>=', Timestamp.fromDate(oneWeekAgo))))
    ]);
    
    return {
      totalResumes: totalResumes.status === 'fulfilled' ? ensureNumber(totalResumes.value.data().count) : 0,
      totalCoverLetters: totalCoverLetters.status === 'fulfilled' ? ensureNumber(totalCoverLetters.value.data().count) : 0,
      resumesCreatedToday: resumesToday.status === 'fulfilled' ? ensureNumber(resumesToday.value.data().count) : 0,
      coverLettersCreatedToday: coverLettersToday.status === 'fulfilled' ? ensureNumber(coverLettersToday.value.data().count) : 0,
      resumesCreatedWeek: resumesWeek.status === 'fulfilled' ? ensureNumber(resumesWeek.value.data().count) : 0,
      coverLettersCreatedWeek: coverLettersWeek.status === 'fulfilled' ? ensureNumber(coverLettersWeek.value.data().count) : 0
    };
  } catch (error) {
    console.error('Error fetching document metrics:', error);
    return {
      totalResumes: 0,
      totalCoverLetters: 0,
      resumesCreatedToday: 0,
      coverLettersCreatedToday: 0,
      resumesCreatedWeek: 0,
      coverLettersCreatedWeek: 0
    };
  }
};

/**
 * Fetch financial metrics with proper currency separation
 */
const fetchFinancialMetrics = async () => {
  try {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    const oneYearAgo = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
    
    // Fetch all purchases data
    const purchasesSnapshot = await getDocs(collection(db, 'purchases'));
    
    let revenueToday_USD = 0, revenueWeek_USD = 0, revenueMonth_USD = 0, revenueYear_USD = 0;
    let revenueToday_INR = 0, revenueWeek_INR = 0, revenueMonth_INR = 0, revenueYear_INR = 0;
    let totalTransactions_USD = 0, totalTransactions_INR = 0;
    let totalAmount_USD = 0, totalAmount_INR = 0;
    
    purchasesSnapshot.forEach(doc => {
      const data = doc.data();
      const createdAt = data.createdAt ? data.createdAt.toDate() : new Date(0);
      const amount = ensureNumber(data.amount, 0);
      const currency = (data.currency || 'usd').toLowerCase();
      
      if (data.paymentStatus === 'paid' || data.status === 'active') {
        const amountInCurrency = amount / 100; // Convert from cents
        
        if (currency === 'usd') {
          totalAmount_USD += amountInCurrency;
          totalTransactions_USD++;
          
          if (createdAt >= today) revenueToday_USD += amountInCurrency;
          if (createdAt >= oneWeekAgo) revenueWeek_USD += amountInCurrency;
          if (createdAt >= oneMonthAgo) revenueMonth_USD += amountInCurrency;
          if (createdAt >= oneYearAgo) revenueYear_USD += amountInCurrency;
        } else if (currency === 'inr') {
          totalAmount_INR += amountInCurrency;
          totalTransactions_INR++;
          
          if (createdAt >= today) revenueToday_INR += amountInCurrency;
          if (createdAt >= oneWeekAgo) revenueWeek_INR += amountInCurrency;
          if (createdAt >= oneMonthAgo) revenueMonth_INR += amountInCurrency;
          if (createdAt >= oneYearAgo) revenueYear_INR += amountInCurrency;
        }
      }
    });
    
    return {
      revenueToday_USD: revenueToday_USD.toFixed(2),
      revenueWeek_USD: revenueWeek_USD.toFixed(2),
      revenueMonth_USD: revenueMonth_USD.toFixed(2),
      revenueYear_USD: revenueYear_USD.toFixed(2),
      averageOrderValue_USD: totalTransactions_USD > 0 ? (totalAmount_USD / totalTransactions_USD).toFixed(2) : "0.00",
      
      revenueToday_INR: revenueToday_INR.toFixed(2),
      revenueWeek_INR: revenueWeek_INR.toFixed(2),
      revenueMonth_INR: revenueMonth_INR.toFixed(2),
      revenueYear_INR: revenueYear_INR.toFixed(2),
      averageOrderValue_INR: totalTransactions_INR > 0 ? (totalAmount_INR / totalTransactions_INR).toFixed(2) : "0.00"
    };
  } catch (error) {
    console.error('Error fetching financial metrics:', error);
    return {
      revenueToday_USD: "0.00",
      revenueWeek_USD: "0.00",
      revenueMonth_USD: "0.00",
      revenueYear_USD: "0.00",
      averageOrderValue_USD: "0.00",
      revenueToday_INR: "0.00",
      revenueWeek_INR: "0.00",
      revenueMonth_INR: "0.00",
      revenueYear_INR: "0.00",
      averageOrderValue_INR: "0.00"
    };
  }
};

/**
 * Fetch payment analytics
 */
const fetchPaymentAnalytics = async () => {
  try {
    const [
      successfulPayments,
      failedPayments,
      paymentAttempts
    ] = await Promise.allSettled([
      getCountFromServer(query(collection(db, 'purchases'), where('paymentStatus', '==', 'paid'))),
      getCountFromServer(collection(db, 'failed-payments')),
      getCountFromServer(collection(db, 'payment-attempts'))
    ]);
    
    const successfulCount = successfulPayments.status === 'fulfilled' ? ensureNumber(successfulPayments.value.data().count) : 0;
    const failedCount = failedPayments.status === 'fulfilled' ? ensureNumber(failedPayments.value.data().count) : 0;
    const attemptCount = paymentAttempts.status === 'fulfilled' ? ensureNumber(paymentAttempts.value.data().count) : 0;
    
    const totalPaymentAttempts = successfulCount + failedCount + attemptCount;
    
    return {
      successfulPayments: successfulCount,
      failedPayments: failedCount,
      paymentAttempts: attemptCount,
      paymentSuccessRate: totalPaymentAttempts > 0 ? ((successfulCount / totalPaymentAttempts) * 100).toFixed(2) : "0.00"
    };
  } catch (error) {
    console.error('Error fetching payment analytics:', error);
    return {
      successfulPayments: 0,
      failedPayments: 0,
      paymentAttempts: 0,
      paymentSuccessRate: "0.00"
    };
  }
};

/**
 * Fetch engagement metrics
 */
const fetchEngagementMetrics = async () => {
  try {
    const [usersSnapshot, resumesSnapshot, coverLettersSnapshot] = await Promise.all([
      getDocs(collection(db, 'users')),
      getDocs(collection(db, 'resumes')),
      getDocs(collection(db, 'cover-letters'))
    ]);
    
    const totalUsers = ensureNumber(usersSnapshot.size);
    const totalDocuments = ensureNumber(resumesSnapshot.size) + ensureNumber(coverLettersSnapshot.size);
    
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    
    // Count active users (users who created documents)
    const activeUserIds = new Set();
    const activeUserIdsWeek = new Set();
    
    resumesSnapshot.forEach(doc => {
      const data = doc.data();
      const createdAt = data.createdAt ? data.createdAt.toDate() : new Date(0);
      if (createdAt >= today && data.userId) activeUserIds.add(data.userId);
      if (createdAt >= oneWeekAgo && data.userId) activeUserIdsWeek.add(data.userId);
    });
    
    coverLettersSnapshot.forEach(doc => {
      const data = doc.data();
      const createdAt = data.createdAt ? data.createdAt.toDate() : new Date(0);
      if (createdAt >= today && data.userId) activeUserIds.add(data.userId);
      if (createdAt >= oneWeekAgo && data.userId) activeUserIdsWeek.add(data.userId);
    });
    
    return {
      averageDocumentsPerUser: totalUsers > 0 ? (totalDocuments / totalUsers).toFixed(2) : "0.00",
      activeUsersToday: activeUserIds.size,
      activeUsersWeek: activeUserIdsWeek.size
    };
  } catch (error) {
    console.error('Error fetching engagement metrics:', error);
    return {
      averageDocumentsPerUser: "0.00",
      activeUsersToday: 0,
      activeUsersWeek: 0
    };
  }
};

/**
 * Admin access check with retry
 */
const checkAdminAccessWithRetry = async (userId, retries = 3) => {
  let lastError;
  
  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      return await checkAdminAccess(userId);
    } catch (error) {
      console.error(`Admin access check attempt ${attempt + 1} failed:`, error);
      lastError = error;
      
      if (attempt < retries - 1) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }
  }
  
  throw lastError;
};

/**
 * Checks if a user has admin access
 */
const checkAdminAccess = async (userId) => {
  try {
    const adminDocRef = doc(db, 'admin', process.env.REACT_APP_ADMIN_DOC_ID || 'IStA6fJzjiHovjcz6B0V');
    const adminDoc = await getDoc(adminDocRef);
    
    if (!adminDoc.exists()) {
      console.error('Admin document does not exist');
      return false;
    }
    
    const adminData = adminDoc.data();
    
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      console.error('User document does not exist');
      return false;
    }
    
    const userData = userDoc.data();
    const userEmail = userData.email;
    
    return adminData.adminUser && adminData.adminUser.includes(userEmail);
  } catch (error) {
    console.error('Error checking admin access:', error);
    return false;
  }
};